import { useState } from 'react';
import useFetch from '../../../hooks/useFetch';
import { buildEndpoint } from '../EndPoints';
import { ServiceEvents } from '../shared/interfaces';

const useContractDraftingService = () => {
    const { doGet, doPost, doDelete, doGetDocument } = useFetch();
    
    // Estados de carga
    const [fetchingContractTypes, setFetchingContractTypes] = useState(false);
    const [fetchingCreateRequest, setFetchingCreateRequest] = useState(false);
    const [fetchingFormConfig, setFetchingFormConfig] = useState(false);
    const [fetchingSaveFormData, setFetchingSaveFormData] = useState(false);
    const [fetchingGeneratePreview, setFetchingGeneratePreview] = useState(false);
    const [fetchingCreatePayment, setFetchingCreatePayment] = useState(false);
    const [fetchingMyRequests, setFetchingMyRequests] = useState(false);
    const [fetchingMySignedContracts, setFetchingMySignedContracts] = useState(false);
    const [fetchingRequestDetail, setFetchingRequestDetail] = useState(false);
    const [fetchingAddSigners, setFetchingAddSigners] = useState(false);
    const [fetchingSendInvitations, setFetchingSendInvitations] = useState(false);
    const [fetchingInvitations, setFetchingInvitations] = useState(false);
    const [fetchingDownload, setFetchingDownload] = useState(false);
    const [fetchingDeleteSigner, setFetchingDeleteSigner] = useState(false);
    
    const BASE_PATH = 'app/contract-drafting';
    
    /**
     * Obtener tipos de contrato disponibles (cliente)
     */
    const getContractTypes = (events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/types-available`),
            setFetching: setFetchingContractTypes
        });
    };
    
    /**
     * Crear nueva solicitud de contrato
     */
    const createContractRequest = (contractTypeId: number, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/requests/create`),
            body: { contract_type_id: contractTypeId },
            requestType: 'json',
            setFetching: setFetchingCreateRequest
        });
    };
    
    /**
     * Obtener configuración del formulario (campos y artículos)
     */
    const getFormConfig = (requestId: number, events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/requests/${requestId}/form-config`),
            setFetching: setFetchingFormConfig
        });
    };
    
    /**
     * Guardar datos del formulario
     */
    const saveFormData = (requestId: number, data: any, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/requests/${requestId}/save-form`),
            body: data,
            requestType: 'json',
            setFetching: setFetchingSaveFormData
        });
    };
    
    /**
     * Generar vista previa del contrato
     */
    const generatePreview = (requestId: number, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/requests/${requestId}/generate-preview`),
            requestType: 'json',
            setFetching: setFetchingGeneratePreview
        });
    };
    
    /**
     * Crear orden de pago Flow
     */
    const createPayment = (requestId: number, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/requests/${requestId}/create-payment`),
            requestType: 'json',
            setFetching: setFetchingCreatePayment
        });
    };
    
    /**
     * Descargar documento final (después de pagar)
     */
    const downloadFinalDocument = (requestId: number, referenceCode?: string) => {
        const fileName = referenceCode ? `contrato_${referenceCode}.pdf` : `contrato_${requestId}.pdf`;
        doGetDocument({
            url: buildEndpoint(`${BASE_PATH}/requests/${requestId}/download-final`),
            setFetching: setFetchingDownload,
            successData: {
                nameDocument: fileName,
                message: 'Documento descargado exitosamente'
            },
            onError: (res: any) => {
                console.error('Error al descargar:', res);
            }
        });
    };
    
    /**
     * Obtener mis solicitudes de contrato
     */
    const getMyContractRequests = (filters: any = {}, events: ServiceEvents = {}) => {
        const queryString = Object.entries(filters)
            .filter(([_, value]) => value !== undefined && value !== '')
            .map(([key, value]) => `${key}=${value}`)
            .join('&');
            
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/my-requests${queryString ? '?' + queryString : ''}`),
            setFetching: setFetchingMyRequests
        });
    };
    
    /**
     * Obtener los contratos donde participo como firmante
     */
    const getMySignedContracts = (filters: any = {}, events: ServiceEvents = {}) => {
        const queryString = Object.entries(filters)
            .filter(([_, value]) => value !== undefined && value !== '')
            .map(([key, value]) => `${key}=${value}`)
            .join('&');
            
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/my-signed-contracts${queryString ? '?' + queryString : ''}`),
            setFetching: setFetchingMySignedContracts
        });
    };
    
    /**
     * Obtener detalle de una solicitud
     */
    const getRequestDetail = (requestId: number, events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/requests/${requestId}/show`),
            setFetching: setFetchingRequestDetail
        });
    };
    
    /**
     * Agregar firmantes a un contrato
     */
    const addSigners = (requestId: number, signers: Array<{email: string, name: string, rut?: string}>, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/requests/${requestId}/signers`),
            body: { signers },
            requestType: 'json',
            setFetching: setFetchingAddSigners
        });
    };
    
    /**
     * Enviar invitaciones de firma por email
     */
    const sendSignatureInvitations = (requestId: number, customMessage?: string, invitationIds: number[] = [],
                                      events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/requests/${requestId}/send-invitations`),
            body: { custom_message: customMessage, ...(invitationIds.length ? { invitation_ids: invitationIds } : {}) },
            requestType: 'json',
            setFetching: setFetchingSendInvitations
        });
    };
    
    /**
     * Obtener invitaciones de firma de un contrato
     */
    const getSignatureInvitations = (requestId: number, events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/requests/${requestId}/signers/list`),
            setFetching: setFetchingInvitations
        });
    };
    
    /**
     * Eliminar un firmante de un contrato
     */
    const deleteSigner = (requestId: number, signerId: number, events: ServiceEvents = {}) => {
        doDelete({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/requests/${requestId}/signers/${signerId}/delete`),
            setFetching: setFetchingDeleteSigner
        });
    };
    
    return {
        // Estados de carga
        fetchingContractTypes,
        fetchingCreateRequest,
        fetchingFormConfig,
        fetchingSaveFormData,
        fetchingGeneratePreview,
        fetchingCreatePayment,
        fetchingMyRequests,
        fetchingMySignedContracts,
        fetchingRequestDetail,
        fetchingAddSigners,
        fetchingSendInvitations,
        fetchingInvitations,
        fetchingDownload,
        fetchingDeleteSigner,
        
        // Métodos
        getContractTypes,
        createContractRequest,
        getFormConfig,
        saveFormData,
        generatePreview,
        createPayment,
        downloadFinalDocument,
        getMyContractRequests,
        getMySignedContracts,
        getRequestDetail,
        addSigners,
        sendSignatureInvitations,
        getSignatureInvitations,
        deleteSigner
    };
};

export default useContractDraftingService;
